import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import { useState } from 'react';

import LazyVideo from '@/components/ui/lazy-video';

const FAQS = [
  {
    question: 'Do I need to sign up before speaking with Chinobi?',
    answer:
      'Chinobi can be accessed through a streamlined signup experience. If you later want access to deeper Agentous features, you may be asked for a few additional details to complete your full account setup.',
  },
  {
    question: 'How long is the complimentary consultation?',
    answer:
      'Your first 10 minutes of live consultation with Chinobi are complimentary.',
  },
  {
    question: 'Will I see a timer during the call?',
    answer:
      'The timer only appears in the last 90 seconds of your complimentary consultation so it does not interrupt the experience.',
  },
  {
    question: 'What happens when the 10 free minutes end?',
    answer:
      'The video call pauses and you’ll be given the option to Extend Consultation or End Call.',
  },
  {
    question: 'How much does it cost to extend the consultation?',
    answer: 'Additional consultation time costs $29 per hour.',
  },
  {
    question: 'What can Chinobi help me with?',
    answer:
      'Chinobi can help you explore best practices across origination, pre-underwriting, underwriting, and collections, while showing where autonomous AI agents can transform specialty finance operations.',
  },
] as const;

export default function ChinobiFaq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="relative overflow-hidden py-16 sm:py-20 lg:py-16">
      <div className="pointer-events-none absolute inset-0">
        <LazyVideo
          src="/suites/CrossChannel.mp4"
          rootMargin="400px"
          preload="metadata"
          className="h-full w-full object-cover"
        />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(0deg, rgba(18, 75, 104, 0.2), rgba(18, 75, 104, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75))',
        }}
      />

      <div className="relative z-10 mx-auto flex w-[90%] max-w-[1344px] flex-col gap-10 lg:min-h-[650px] lg:flex-row lg:items-center lg:gap-12">
        <div className="lg:w-[480px] lg:shrink-0">
          <h2 className="max-w-[380px] text-center font-fraunces text-2xl font-bold leading-[28px] text-white sm:text-left sm:text-[40px] sm:leading-[48px]">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="w-full overflow-hidden rounded-2xl bg-white md:rounded-[25px]">
          {FAQS.map((faq, index) => {
            const isOpen = index === openIndex;

            return (
              <div
                key={faq.question}
                className="border-b border-zinc-200 p-5 last:border-0 md:p-6 lg:p-[30px]"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 text-left outline-none transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                >
                  <span className="font-fraunces text-base leading-6 text-[#12141d] sm:text-lg sm:leading-[30px] lg:text-[21px]">
                    {faq.question}
                  </span>
                  {isOpen ? (
                    <Minus
                      className="h-5 w-5 shrink-0 text-[#12141d]"
                      aria-hidden="true"
                    />
                  ) : (
                    <Plus
                      className="h-5 w-5 shrink-0 text-[#12141d]"
                      aria-hidden="true"
                    />
                  )}
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="max-w-[700px] overflow-hidden"
                    >
                      <p className="pt-3 text-sm leading-6 text-zinc-600 sm:text-base">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
